import {SubscriptionLike} from 'rxjs';

interface ManagedSubscription {
  id: string;
  subscription: SubscriptionLike;
  metadata?: Record<string, unknown>;
}

/**
 * SubscriptionManager provides centralized lifecycle management for RxJS subscriptions.
 *
 * Prevents memory leaks by:
 * - Tracking all subscriptions with unique IDs
 * - Supporting bulk unsubscribe operations
 * - Providing automatic cleanup on destroy
 */
export class SubscriptionManager {
  private subscriptions = new Map<string, ManagedSubscription>();
  private autoId = 0;

  /**
   * Add a subscription to be managed.
   * Returns the subscription ID for later removal.
   */
  add(subscription: SubscriptionLike, id?: string, metadata?: Record<string, unknown>): string {
    const subId = id || `sub_${++this.autoId}`;

    // Unsubscribe existing subscription with same ID
    if (this.subscriptions.has(subId)) {
      this.remove(subId);
    }

    this.subscriptions.set(subId, {
      id: subId,
      subscription,
      metadata
    });

    return subId;
  }

  /**
   * Remove and unsubscribe a specific subscription.
   */
  remove(id: string): boolean {
    const managed = this.subscriptions.get(id);
    if (!managed) {
      return false;
    }

    this.unsubscribeManaged(managed);
    this.subscriptions.delete(id);
    return true;
  }

  /**
   * Unsubscribe all managed subscriptions.
   */
  unsubscribeAll(): void {
    for (const managed of this.subscriptions.values()) {
      this.unsubscribeManaged(managed);
    }
    this.subscriptions.clear();
  }

  /**
   * Unsubscribe subscriptions matching a predicate.
   */
  unsubscribeWhere(predicate: (metadata: Record<string, unknown> | undefined) => boolean): number {
    let count = 0;
    const toRemove: string[] = [];

    for (const [id, managed] of this.subscriptions) {
      if (predicate(managed.metadata)) {
        this.unsubscribeManaged(managed);
        toRemove.push(id);
        count++;
      }
    }

    for (const id of toRemove) {
      this.subscriptions.delete(id);
    }

    return count;
  }

  /**
   * Get count of active subscriptions.
   */
  get count(): number {
    return this.subscriptions.size;
  }

  /**
   * Check if a subscription ID exists.
   */
  has(id: string): boolean {
    return this.subscriptions.has(id);
  }

  /**
   * Get metadata for a subscription.
   */
  getMetadata(id: string): Record<string, unknown> | undefined {
    return this.subscriptions.get(id)?.metadata;
  }

  private unsubscribeManaged(managed: ManagedSubscription): void {
    if (managed.subscription && !managed.subscription.closed) {
      managed.subscription.unsubscribe();
    }
  }
}

/**
 * Mixin for classes that need subscription management.
 * Provides automatic cleanup pattern.
 */
export function WithSubscriptionManager<T extends new (...args: any[]) => any>(Base: T) {
  return class extends Base {
    protected subscriptions = new SubscriptionManager();

    destroy(): void {
      this.subscriptions.unsubscribeAll();
      if (typeof super['destroy'] === 'function') {
        super['destroy']();
      }
    }
  };
}
